import React, { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Box, Typography, Paper, Button, Grid, Chip, CircularProgress, Alert } from '@mui/material';
import { tenantApiFetch } from './tenantApi';

function statusColor(s) {
  if (s === 'paid') return 'success';
  if (s === 'overdue' || s === 'void') return 'error';
  if (s === 'draft') return 'default';
  return 'info';
}

export default function TenantInvoiceDetailPage() {
  const { invoiceId } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await tenantApiFetch(`/api/tenant/billing/invoices/${invoiceId}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Not found');
      setInvoice(json.invoice);
    } catch (e) {
      setError(e.message);
      setInvoice(null);
    } finally {
      setLoading(false);
    }
  }, [invoiceId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !invoice) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!invoice) return null;

  const row = (label, value) => (
    <Grid item xs={12} sm={6}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1">{value ?? '—'}</Typography>
    </Grid>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5">Invoice {invoice.invoice_reference}</Typography>
          <Chip size="small" label={invoice.invoice_status} color={statusColor(invoice.invoice_status)} sx={{ mt: 1 }} />
        </Box>
        <Button variant="outlined" component={Link} to="/tenant/billing/invoices">
          Back to invoices
        </Button>
      </Box>
      <Paper sx={{ p: 2 }}>
        <Grid container spacing={2}>
          {row('Amount', invoice.amount != null ? `${invoice.amount.toFixed(2)} ${invoice.currency}` : '—')}
          {row('Billing period', invoice.billing_period)}
          {row('Issued', invoice.issued_at ? String(invoice.issued_at) : '—')}
          {row('Due', invoice.due_at ? String(invoice.due_at) : '—')}
          {row('Paid', invoice.paid_at ? String(invoice.paid_at) : '—')}
          {row('Created', invoice.created_at ? String(invoice.created_at) : '—')}
        </Grid>
      </Paper>
    </Box>
  );
}
